/**
 * Driving the app with a controller.
 *
 * Bigger targets and a visible focus ring made Pugo *readable* from a sofa;
 * they did not make it usable, because a controller cannot hover and cannot
 * click. This is the other half: the stick and the d-pad move focus to
 * whatever is nearest in that direction, A presses it, B goes back.
 *
 * Focus moves geometrically rather than in DOM order. A row of party cards is
 * a row on the screen and nowhere in the markup, and "down" has to mean down -
 * tab order would send you through a card's three buttons before reaching the
 * card beside it.
 *
 * It only runs while the setting is on and a pad is actually connected, and
 * it stops the moment either stops being true: polling every frame for a
 * controller nobody owns is exactly the sort of thing that drains a laptop.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type=hidden])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/** A press has to end before it counts again - a held stick is not a queue. */
type Held = Record<string, boolean>;

function visible(el: Element): boolean {
  const box = el.getBoundingClientRect();
  if (!box.width || !box.height) return false;
  if (box.bottom < 0 || box.top > window.innerHeight) return false;
  if (box.right < 0 || box.left > window.innerWidth) return false;
  const style = getComputedStyle(el);
  return style.visibility !== 'hidden' && style.display !== 'none' && Number(style.opacity) > 0.05;
}

function candidates(): HTMLElement[] {
  /* Inside an open dialog, only the dialog - the same rule the keyboard
     already follows, so a controller cannot wander behind a modal. */
  const modal = document.querySelector<HTMLElement>('[role="dialog"]');
  const root: ParentNode = modal ?? document.body;
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(visible);
}

/** The nearest thing in that direction, by distance with a strong angle bias. */
function nearest(from: HTMLElement, dir: 'up' | 'down' | 'left' | 'right'): HTMLElement | null {
  const a = from.getBoundingClientRect();
  const ax = a.left + a.width / 2;
  const ay = a.top + a.height / 2;

  let best: HTMLElement | null = null;
  let bestScore = Infinity;

  for (const el of candidates()) {
    if (el === from) continue;
    const b = el.getBoundingClientRect();
    const bx = b.left + b.width / 2;
    const by = b.top + b.height / 2;
    const dx = bx - ax;
    const dy = by - ay;

    /* Must actually be in that direction, by more than a few pixels. */
    const along = dir === 'left' ? -dx : dir === 'right' ? dx : dir === 'up' ? -dy : dy;
    if (along < 6) continue;
    const across = Math.abs(dir === 'left' || dir === 'right' ? dy : dx);

    /* Distance along the direction, plus a heavy penalty for drifting
       sideways: "down" should reach the thing below, not the one diagonally
       across the screen that happens to be closer as the crow flies. */
    const score = along + across * 2.2;
    if (score < bestScore) { bestScore = score; best = el; }
  }
  return best;
}

function press(el: HTMLElement) {
  /* A real click, so every handler in the app sees what it expects. */
  el.click();
}

/** Escape, for a controller: closes whatever is open, or goes back. */
function back() {
  const esc = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true });
  document.dispatchEvent(esc);
  document.activeElement?.dispatchEvent(esc);
}

let running = false;
let frame = 0;
const held: Held = {};

function step() {
  if (!running) return;
  frame = requestAnimationFrame(step);

  const pads = navigator.getGamepads ? [...navigator.getGamepads()] : [];
  const pad = pads.find((p) => p && p.connected);
  if (!pad) return;

  const [lx = 0, ly = 0] = pad.axes;
  const dead = 0.55;
  const up = pad.buttons[12]?.pressed || ly < -dead;
  const down = pad.buttons[13]?.pressed || ly > dead;
  const left = pad.buttons[14]?.pressed || lx < -dead;
  const right = pad.buttons[15]?.pressed || lx > dead;
  const a = pad.buttons[0]?.pressed;
  const b = pad.buttons[1]?.pressed;

  const edge = (name: string, on: boolean | undefined) => {
    const was = held[name];
    held[name] = !!on;
    return !!on && !was;
  };

  const current = (document.activeElement as HTMLElement | null);
  const from = current && current !== document.body && visible(current)
    ? current
    : candidates()[0] ?? null;

  for (const [name, on, dir] of [
    ['up', up, 'up'], ['down', down, 'down'], ['left', left, 'left'], ['right', right, 'right']
  ] as const) {
    if (!edge(name, on)) continue;
    if (!from) continue;
    const next = nearest(from, dir);
    if (next) {
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    return;
  }

  if (edge('a', a) && from) press(from);
  if (edge('b', b)) back();
}

/**
 * Starts or stops the driver.
 *
 * Called whenever the setting changes and whenever a pad is plugged in or
 * pulled out, so nothing polls unless both are true.
 */
export function gamepadNav(enabled: boolean): void {
  const pads = navigator.getGamepads ? [...navigator.getGamepads()] : [];
  const want = enabled && pads.some((p) => p && p.connected);

  if (want && !running) {
    running = true;
    document.body.classList.add('pad-on');
    frame = requestAnimationFrame(step);
  } else if (!want && running) {
    running = false;
    document.body.classList.remove('pad-on');
    cancelAnimationFrame(frame);
    for (const key of Object.keys(held)) held[key] = false;
  }
}

/** Wires the two events that change whether it should run. Returns a cleanup. */
export function watchGamepads(isEnabled: () => boolean): () => void {
  const sync = () => gamepadNav(isEnabled());
  window.addEventListener('gamepadconnected', sync);
  window.addEventListener('gamepaddisconnected', sync);
  sync();
  return () => {
    window.removeEventListener('gamepadconnected', sync);
    window.removeEventListener('gamepaddisconnected', sync);
    gamepadNav(false);
  };
}
